'use client';

import React, { useEffect, useRef } from 'react';
import dynamic from 'next/dynamic';
import { useRouter, usePathname } from 'next/navigation';
import { Drawer, Box, useMediaQuery, useTheme } from '@mui/material';
import { useAgentContext } from '../../context/AgentContext';
import { useAgentWebSocket } from '../../hooks/useAgentWebSocket';
import { useAiAssistantEnabled } from '../../hooks/useAiAssistantEnabled';
import { agentNavAllowlist, useAgentNavigation } from '../../hooks/useAgentNavigation';
import { subscribe, unsubscribe } from '@/lib/events';
import { resolveAgentNavigatePath } from '../../utils/agentNavigation';
import AgentPanelHeader from './AgentPanelHeader';
import ChatInput from '../Chat/ChatInput';
import CreditUsageMeter from '../Chat/CreditUsageMeter';
import WelcomeScreen from '../WelcomeScreen';
import './AgentPanel.scss';

// Dynamic import to avoid ESM-only react-markdown blocking initial render
const ChatContainer = dynamic(() => import('../Chat/ChatContainer'), { ssr: false });

const HEADER_HEIGHT = 64;

export default function AgentPanel() {
  const isEnabled = useAiAssistantEnabled();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const router = useRouter();
  const pathname = usePathname();
  const { state, closePanel, restoreLastConversation } = useAgentContext();
  const { connect, disconnect } = useAgentWebSocket();

  // Stable refs for connect/disconnect to avoid effect loops
  const connectRef = useRef(connect);
  const disconnectRef = useRef(disconnect);
  connectRef.current = connect;
  disconnectRef.current = disconnect;

  // Stable ref for restoreLastConversation
  const restoreRef = useRef(restoreLastConversation);
  restoreRef.current = restoreLastConversation;

  // Connect WebSocket when panel opens
  useEffect(() => {
    if (state.panelOpen) {
      connectRef.current();
      // Auto-restore last conversation if panel is empty
      if (state.messages.length === 0 && !state.activeConversationId) {
        restoreRef.current();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.panelOpen]);

  // Disconnect once the panel is closed and no reply is streaming. An agent navigation closes the
  // panel mid-reply, and dropping the socket then would lose the rest of the answer.
  useEffect(() => {
    if (!state.panelOpen && !state.isStreaming) {
      disconnectRef.current();
    }
  }, [state.panelOpen, state.isStreaming]);

  // Agent-triggered page navigation — lives here because AgentPanel is always mounted (root layout).
  // Close the panel first so the user sees the target page, then navigate after a short delay
  // to let React's concurrent render cycle settle (immediate push can get swallowed by streaming).
  const routerRef = useRef(router);
  routerRef.current = router;
  const pathnameRef = useRef(pathname);
  pathnameRef.current = pathname;
  const closePanelRef = useRef(closePanel);
  closePanelRef.current = closePanel;
  const navigationPages = useAgentNavigation();
  const navigationPagesRef = useRef(navigationPages);
  navigationPagesRef.current = navigationPages;

  useEffect(() => {
    const handleAgentNavigate = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      const target = resolveAgentNavigatePath(
        detail?.path,
        pathnameRef.current,
        agentNavAllowlist(navigationPagesRef.current)
      );
      if (!target) return;
      closePanelRef.current();
      setTimeout(() => routerRef.current.push(target), 150);
    };

    subscribe('agent:navigate-to', handleAgentNavigate);
    return () => unsubscribe('agent:navigate-to', handleAgentNavigate);
  }, []);

  if (!isEnabled) return null;

  const hasMessages = state.messages.length > 0 || state.isStreaming;

  return (
    <Drawer
      variant="temporary"
      anchor="right"
      open={state.panelOpen}
      onClose={closePanel}
      ModalProps={{ className: 'agent-panel-wrapper' }}
      PaperProps={{
        className: 'agent-panel',
        sx: {
          width: isMobile ? '100vw' : 400,
          top: `${HEADER_HEIGHT}px`,
          height: `calc(100vh - ${HEADER_HEIGHT}px)`,
          bgcolor: 'var(--secondary-color)',
          borderLeft: '1px solid rgba(244, 246, 255, 0.08)',
        },
      }}
    >
      <AgentPanelHeader />

      <Box className="agent-panel-body">{hasMessages ? <ChatContainer /> : <WelcomeScreen />}</Box>

      <Box className="agent-panel-footer">
        <CreditUsageMeter />
        <ChatInput />
      </Box>
    </Drawer>
  );
}
